import React, { useState, useEffect, useCallback } from 'react';
import { createClient } from '@supabase/supabase-js';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

// === SUPABASE (VITE) ===
const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL,
  import.meta.env.VITE_SUPABASE_ANON_KEY
);

// === FUNCIÓN PARA FORMATEAR FECHA ===
const formatDate = (dateString) => {
  if (!dateString) return 'Fecha no disponible';
  
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Fecha inválida';
    
    return date.toLocaleDateString('es-VE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Fecha inválida';
  }
};

// === FUNCIÓN PARA OBTENER TIEMPO RELATIVO ===
const getRelativeTime = (dateString) => {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Ahora mismo';
    if (diffInMinutes < 60) return `Hace ${diffInMinutes} min`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `Hace ${diffInHours} h`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    return `Hace ${diffInDays} d`;
  } catch (error) {
    return '';
  }
};

// === COMPONENTE MEJORADO PARA MÚLTIPLES COMPRAS ===
const TicketResults = ({
  searchType,
  searchValue,
  onClearResults,
  className = ''
}) => {
  const [purchasesData, setPurchasesData] = useState(null); // { all, confirmed, pending }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'confirmed', 'pending'
  const [currentPurchaseIndex, setCurrentPurchaseIndex] = useState(0);

  const load = useCallback(async () => {
    if (searchType !== 'cedula' || !searchValue) return;

    const clean = searchValue.replace(/\s|-/g, '');
    setLoading(true);
    setError(null);
    setPurchasesData(null);
    setShowSuccess(false);
    setActiveTab('all');

    try {
      console.log('🚀 Iniciando búsqueda mejorada:', clean);
      
      // Buscar TODAS las compras del cliente
      const { data: purchases, error } = await supabase
        .from('purchases')
        .select('*')
        .eq('cedula', clean)
        .in('estado', ['pendiente', 'completado', 'confirmado'])
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      if (!purchases || purchases.length === 0) {
        setError('Cédula no encontrada');
        return;
      }

      console.log('📊 Total de compras encontradas:', purchases.length);
      
      // Separar por estado
      const all = purchases;
      const confirmed = all.filter(p => p.estado === 'confirmado' || p.estado === 'completado');
      const pending = all.filter(p => p.estado === 'pendiente');
      
      console.log('✅ Compras confirmadas:', confirmed.length);
      console.log('⏳ Compras pendientes:', pending.length);
      
      setPurchasesData({ all, confirmed, pending });
      setShowSuccess(true);

    } catch (err) {
      setError('Error al conectar');
      console.error('❌ Error:', err);
    } finally {
      setLoading(false);
    }
  }, [searchValue, searchType]);

  useEffect(() => {
    const t = setTimeout(load, 600);
    return () => clearTimeout(t);
  }, [load]);

  // === RENDERIZAR COMPRA INDIVIDUAL ===
  const renderPurchase = (purchase, index, total) => {
    const raw = purchase.numeros;
    const numeros = typeof raw === 'string'
      ? raw.trim()
      : Array.isArray(raw) ? raw.join(', ').trim() : '';
    
    const estado = purchase.estado === 'completado' ? 'confirmado' : purchase.estado;
    const isPendiente = estado === 'pendiente';
    const isConfirmado = estado === 'confirmado';
    
    return (
      <div key={purchase.id} className={`bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-3xl border ${isPendiente ? 'border-orange-500/50' : 'border-green-500/50'} p-8 shadow-2xl mb-6`}>
        
        {/* Header con información de compra */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className={`relative p-4 rounded-2xl ${isPendiente ? 'bg-orange-500/20' : 'bg-green-500/20'} border ${isPendiente ? 'border-orange-500/30' : 'border-green-500/30'}`}>
              <Icon name={isPendiente ? 'Clock' : 'CheckCircle'} size={28} className={`${isPendiente ? 'text-orange-400' : 'text-green-400'}`} />
            </div>
            
            <div>
              <h3 className="text-white font-bold text-xl mb-1">
                Compra #{index + 1} de {total}
              </h3>
              <div className="flex items-center gap-3">
                <p className={`text-sm font-bold uppercase tracking-wide ${isPendiente ? 'text-orange-400' : 'text-green-400'}`}>
                  {estado}
                </p>
                <div className="flex items-center gap-1 text-xs text-gray-400">
                  <Icon name="Calendar" size={12} />
                  <span>{getRelativeTime(purchase.created_at)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Información del propietario en card separada */}
        <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-2xl p-6 border border-blue-500/20 mb-6 backdrop-blur-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <Icon name="User" size={20} className="text-blue-400" />
              </div>
              <div>
                <p className="text-blue-400 text-xs uppercase tracking-wide font-bold">Propietario</p>
                <p className="text-white font-semibold text-lg">{purchase.nombre_completo || 'No disponible'}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              iconName="Copy"
              className="text-gray-400 hover:text-blue-400 hover:bg-blue-400/10"
              onClick={() => {
                navigator.clipboard.writeText(purchase.nombre_completo || '');
                alert('¡Nombre copiado!');
              }}
            >
              Copiar
            </Button>
          </div>
        </div>

        {/* Números destacados */}
        <div className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 rounded-2xl p-8 border border-yellow-400/30 shadow-2xl backdrop-blur-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-yellow-400/20 rounded-lg">
                <Icon name="Hash" size={20} className="text-yellow-400" />
              </div>
              <span className="text-gray-300 text-sm font-bold uppercase tracking-wide">Números de Boletos</span>
            </div>
            <div className="text-xs text-gray-400">
              {numeros.split(',').length} número{numeros.split(',').length !== 1 ? 's' : ''}
            </div>
          </div>
          
          <div className="bg-gray-900/50 rounded-xl p-6 border border-yellow-400/20 shadow-inner">
            <div className="font-mono text-2xl text-yellow-300 break-all leading-relaxed font-bold tracking-wider">
              {numeros}
            </div>
          </div>
        </div>

        {/* Fecha de compra */}
        {purchase.created_at && (
          <div className="mt-6 bg-gradient-to-r from-gray-700/30 to-gray-800/30 rounded-xl p-4 border border-gray-600/30 backdrop-blur-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-500/20 rounded-lg">
                  <Icon name="Calendar" size={16} className="text-purple-400" />
                </div>
                <div>
                  <p className="text-purple-400 text-xs uppercase tracking-wide font-bold">Fecha de Compra</p>
                  <p className="text-white font-medium">{formatDate(purchase.created_at)}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-gray-400 text-xs">Hace</p>
                <p className="text-white font-bold">{getRelativeTime(purchase.created_at)}</p>
              </div>
            </div>
          </div>
        )}

        {/* Botones de acción */}
        <div className="mt-8 flex gap-4 justify-center">
          <Button
            variant="outline"
            size="sm"
            iconName="Copy"
            className="border-yellow-400 text-yellow-400 hover:bg-yellow-400 hover:text-black"
            onClick={() => {
              navigator.clipboard.writeText(numeros);
              alert('¡Números copiados!');
            }}
          >
            <Icon name="Hash" size={16} className="mr-2" />
            Copiar Números
          </Button>
          
          <Button
            variant="ghost"
            size="sm"
            iconName="User"
            className="border-blue-400 text-blue-400 hover:bg-blue-400/20"
            onClick={() => {
              navigator.clipboard.writeText(`${purchase.nombre_completo || ''} - ${numeros}`);
              alert('¡Datos copiados!');
            }}
          >
            <Icon name="User" size={16} className="mr-2" />
            Copiar Todo
          </Button>
        </div>
      </div>
    );
  };

  // === UI LOADING ===
  if (loading) {
    return (
      <div className={`relative ${className}`}>
        <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-3xl border border-gray-600 p-12 text-center shadow-2xl relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-yellow-400/5 to-green-400/5 animate-pulse"></div>
          
          <div className="relative z-10">
            <div className="relative inline-block mb-6">
              <div className="w-16 h-16 border-4 border-gray-600 rounded-full"></div>
              <div className="w-16 h-16 border-4 border-yellow-400 border-t-transparent rounded-full animate-spin absolute top-0 left-0"></div>
              <div className="absolute inset-0 bg-yellow-400/10 rounded-full animate-ping"></div>
            </div>
            
            <h3 className="text-xl font-bold text-white mb-2">Consultando Todas las Compras</h3>
            <p className="text-gray-300 font-medium">Cédula: <span className="text-yellow-400">{searchValue}</span></p>
            
            <div className="flex items-center justify-center space-x-2 mt-4">
              <div className="w-2 h-2 bg-yellow-400 rounded-full animate-bounce"></div>
              <div className="w-2 h-2 bg-yellow-400 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
              <div className="w-2 h-2 bg-yellow-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // === UI ERROR ===
  if (error) {
    return (
      <div className={`relative ${className}`}>
        <div className="bg-gradient-to-br from-red-900/30 via-red-800/20 to-red-900/30 border border-red-600 rounded-2xl p-8 text-center shadow-2xl relative overflow-hidden">
          <div className="absolute inset-0 bg-red-400/5 animate-pulse"></div>
          
          <div className="relative z-10">
            <div className="relative inline-block mb-4">
              <Icon name="AlertTriangle" size={40} className="text-red-400" />
              <div className="absolute inset-0 bg-red-400/20 rounded-full animate-ping"></div>
            </div>
            <h3 className="text-xl font-bold text-red-400 mb-3">No se encontraron compras</h3>
            <p className="text-gray-300 mb-6 font-medium">{error}</p>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={load}
              className="border-red-400 text-red-400 hover:bg-red-400 hover:text-black"
            >
              <Icon name="RotateCcw" size={16} className="mr-2" />
              Reintentar
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!purchasesData) return null;

  const { all, confirmed, pending } = purchasesData;
  
  // Si solo hay una compra, mostrarla normalmente
  if (all.length === 1) {
    return (
      <div className={`relative ${className}`}>
        {showSuccess && (
          <div className="absolute inset-0 bg-gradient-to-r from-green-400/20 to-emerald-400/20 rounded-3xl animate-ping z-0"></div>
        )}
        
        <div className="relative z-10">
          {renderPurchase(all[0], 0, 1)}
        </div>
      </div>
    );
  }

  // Si hay múltiples compras, mostrar con tabs y navegación
  return (
    <div className={`relative ${className}`}>
      {showSuccess && (
        <div className="absolute inset-0 bg-gradient-to-r from-green-400/20 to-emerald-400/20 rounded-3xl animate-ping z-0"></div>
      )}

      <div className="relative z-10">
        {/* Header con resumen */}
        <div className="bg-gradient-to-r from-indigo-500/10 to-purple-500/10 rounded-2xl p-6 border border-indigo-500/20 mb-6 backdrop-blur-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-indigo-500/20 rounded-xl">
                <Icon name="Users" size={24} className="text-indigo-400" />
              </div>
              <div>
                <h2 className="text-white font-bold text-2xl">
                  Cédula: <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-400">{searchValue}</span>
                </h2>
                <p className="text-indigo-300 font-medium">
                  {all.length} compra{all.length !== 1 ? 's' : ''} encontrada{all.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={onClearResults} 
              iconName="X"
              className="text-gray-400 hover:text-white hover:bg-gray-700"
            >
              Cerrar
            </Button>
          </div>

          {/* Estadísticas rápidas */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-blue-500/10 rounded-xl p-4 border border-blue-500/20">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="List" size={16} className="text-blue-400" />
                <span className="text-blue-400 text-sm font-bold">TOTAL</span>
              </div>
              <p className="text-2xl font-bold text-white">{all.length}</p>
            </div>
            
            <div className="bg-green-500/10 rounded-xl p-4 border border-green-500/20">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="CheckCircle" size={16} className="text-green-400" />
                <span className="text-green-400 text-sm font-bold">CONFIRMADAS</span>
              </div>
              <p className="text-2xl font-bold text-white">{confirmed.length}</p>
            </div>
            
            <div className="bg-orange-500/10 rounded-xl p-4 border border-orange-500/20">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="Clock" size={16} className="text-orange-400" />
                <span className="text-orange-400 text-sm font-bold">PENDIENTES</span>
              </div>
              <p className="text-2xl font-bold text-white">{pending.length}</p>
            </div>
          </div>
        </div>

        {/* Tabs para filtrar */}
        <div className="bg-gray-800/50 rounded-xl p-2 mb-6 border border-gray-600/30">
          <div className="flex gap-2">
            {[
              { key: 'all', label: 'Todas', count: all.length, icon: 'List' },
              { key: 'confirmed', label: 'Confirmadas', count: confirmed.length, icon: 'CheckCircle' },
              { key: 'pending', label: 'Pendientes', count: pending.length, icon: 'Clock' }
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-medium transition-all ${
                  activeTab === tab.key
                    ? 'bg-indigo-500 text-white shadow-lg'
                    : 'text-gray-300 hover:text-white hover:bg-gray-700/50'
                }`}
              >
                <Icon name={tab.icon} size={16} />
                <span>{tab.label}</span>
                <span className={`text-xs px-2 py-1 rounded-full ${
                  activeTab === tab.key ? 'bg-white/20' : 'bg-gray-600'
                }`}>
                  {tab.count}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Lista de compras con scroll */}
        <div className="max-h-96 overflow-y-auto space-y-4 pr-2">
          {(activeTab === 'all' ? all : activeTab === 'confirmed' ? confirmed : pending)
            .map((purchase, index) => renderPurchase(purchase, index, (activeTab === 'all' ? all : activeTab === 'confirmed' ? confirmed : pending).length))}
        </div>

        {((activeTab === 'all' ? all : activeTab === 'confirmed' ? confirmed : pending).length === 0) && (
          <div className="text-center py-12">
            <Icon name="Inbox" size={48} className="text-gray-500 mx-auto mb-4" />
            <p className="text-gray-400 font-medium">
              No hay compras en esta categoría
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TicketResults;